import React, { useState } from 'react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Code2, Eye, Layers } from 'lucide-react';
import MermaidDiagram from './MermaidDiagram';
import MarkdownRenderer from './MarkdownRenderer';

export default function CodePreview({ content, phase, project }) {
  const [previewMode, setPreviewMode] = useState('rendered');

  // Extract code blocks from content
  const extractCodeBlocks = (text) => {
    if (!text) return [];
    const regex = /```(\w+)?\n([\s\S]*?)```/g;
    const blocks = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      blocks.push({ language: match[1] || 'text', code: match[2].trim() });
    }
    return blocks;
  };

  // Extract mermaid blocks
  const extractMermaidBlocks = (text) => {
    if (!text) return [];
    const regex = /```mermaid\n([\s\S]*?)```/g;
    const blocks = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      blocks.push(match[1].trim());
    }
    return blocks;
  };

  const codeBlocks = extractCodeBlocks(content);
  const mermaidBlocks = extractMermaidBlocks(content);
  const nonMermaidCode = codeBlocks.filter(b => b.language !== 'mermaid');

  const phaseLabels = {
    requirements: 'Requirements Preview',
    rsd: 'RSD Document',
    fsd: 'FSD Document',
    design: 'Design Preview',
    architecture: 'Architecture View',
    domain: 'Data Model',
    technology: 'Tech Stack',
    code: 'Code Preview',
    tests: 'Test Cases',
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="code-preview">
      {/* Preview Mode Tabs */}
      <div className="px-3 py-1 border-b border-[var(--surface-border)]">
        <div className="flex gap-1">
          <button onClick={() => setPreviewMode('rendered')} className={`text-[10px] px-2 py-1 rounded ${previewMode === 'rendered' ? 'bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)]' : 'opacity-40 hover:opacity-60'}`} data-testid="preview-rendered">
            <Eye className="w-3 h-3 inline mr-1" />Rendered
          </button>
          <button onClick={() => setPreviewMode('code')} className={`text-[10px] px-2 py-1 rounded ${previewMode === 'code' ? 'bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)]' : 'opacity-40 hover:opacity-60'}`} data-testid="preview-code">
            <Code2 className="w-3 h-3 inline mr-1" />Code
          </button>
          {mermaidBlocks.length > 0 && (
            <button onClick={() => setPreviewMode('diagrams')} className={`text-[10px] px-2 py-1 rounded ${previewMode === 'diagrams' ? 'bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)]' : 'opacity-40 hover:opacity-60'}`} data-testid="preview-diagrams">
              <Layers className="w-3 h-3 inline mr-1" />Diagrams
            </button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {!content ? (
            <div className="text-center py-16" data-testid="preview-empty">
              <Code2 className="w-10 h-10 opacity-10 mx-auto mb-3" />
              <p className="text-sm opacity-30">{phaseLabels[phase] || 'Preview'}</p>
              <p className="text-xs opacity-20 mt-1">Content will appear here as you generate</p>
            </div>
          ) : previewMode === 'rendered' ? (
            <div className="space-y-4" data-testid="preview-rendered-content">
              {/* Render structured content with full Markdown support */}
              <MarkdownRenderer content={content} />
            </div>
          ) : previewMode === 'code' ? (
            <div className="space-y-4" data-testid="preview-code-content">
              {nonMermaidCode.length > 0 ? (
                nonMermaidCode.map((block, i) => (
                  <div key={i} className="rounded-lg overflow-hidden border border-[var(--surface-border)]">
                    <div className="bg-[var(--surface-card)] px-3 py-1.5 border-b border-[var(--surface-border)] flex items-center justify-between">
                      <Badge variant="outline" className="text-[9px]">{block.language}</Badge>
                      <button className="text-[10px] opacity-40 hover:opacity-80" onClick={() => navigator.clipboard.writeText(block.code).then(() => {})}>
                        Copy
                      </button>
                    </div>
                    <pre className="p-3 text-[11px] leading-relaxed overflow-x-auto font-mono" style={{fontFamily: 'JetBrains Mono, monospace'}}>
                      <code>{block.code}</code>
                    </pre>
                  </div>
                ))
              ) : (
                <pre className="text-[11px] leading-relaxed whitespace-pre-wrap font-mono p-3 bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg" style={{fontFamily: 'JetBrains Mono, monospace'}} data-testid="raw-code">
                  {content}
                </pre>
              )}
            </div>
          ) : previewMode === 'diagrams' ? (
            <div className="space-y-4" data-testid="preview-diagrams-content">
              {mermaidBlocks.map((chart, i) => (
                <div key={i}>
                  <p className="text-[10px] opacity-30 mb-1">Diagram {i + 1}</p>
                  <MermaidDiagram chart={chart} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}
